import uniq from 'lodash/uniq';
import pick from 'lodash/pick';

import {FunnelAttribution, QueryBuilderUpdateDetail} from 'common/query-builder/query-builder/types';
import {
  CONVERSION_WINDOW_SIZE_LIMITS,
  CONVERSION_WINDOW_UNITS,
  MAX_HOLDING_PROPERTY_CONSTANT,
  ResourceType,
} from 'common/report/constants';
import {
  ATTRIBUTION_OPTIONS,
  ATTRIBUTION_TYPE_TOOLTIPS,
  ConversionWindowUnit,
  FunnelOrder,
} from 'common/report/funnels/models/constants';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {CommonToFunnelChartTypeMap, CountType, FunnelToCommonChartTypeMap} from 'common/report/funnels/models/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ConversionCriteria, QueryContext} from 'common/query-context';
import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {EventItem} from 'common/query-builder/query-block/events-menu/types';
import {FiltersOperator, isGroupedEntry, QueryEventEntry} from 'common/query-builder/query-entry-section/types';
import {getDefaultFunnelStepsSortConfig} from 'common/report/funnels/util';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {useReportConsistency, useSingleReport} from 'common/report/util/feature-sampling';
import {BuilderParams} from 'common/query-builder/conversion-query-options/types';
import {EventAttributionItem} from 'common/report/retention/util/format';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {MetricType} from 'common/query-builder/query-block/constants';

export const MaxNumGroupBys = 3;

export function getConversionQueryOptionsAttrs({
  projectId,
  workspaceId,
  funnelsQuery,
  queryBuilderParams,
}: {
  projectId: number;
  workspaceId: number;
  funnelsQuery: FunnelsQuery;
  queryBuilderParams: BuilderParams;
}) {
  const uniqueEventNames = uniq(
    funnelsQuery.events.flatMap(({event}) => {
      if (Array.isArray(event)) {
        return event.map((evt) => evt.event.value);
      } else {
        return event.value;
      }
    }),
  );

  const optionsData = pick(funnelsQuery, [
    `conversionWindow`,
    `countType`,
    `holdingPropertyConstant`,
    `funnelOrder`,
    `funnelAttributionSelection`,
  ]);

  return {
    'hide-count-selector': useReportConsistency(),
    'project-id': projectId,
    'workspace-id': workspaceId,
    'event-names': uniqueEventNames,
    'include-custom-properties': `all`,
    selected: optionsData,
    'show-sessions': true,
    'builder-params': queryBuilderParams,
    'max-holding-property-constant': MAX_HOLDING_PROPERTY_CONSTANT,
    'conversion-window-units': CONVERSION_WINDOW_UNITS,
    'conversion-window-size-limits': CONVERSION_WINDOW_SIZE_LIMITS,
    'hide-any-order': false,
    'hide-conversion-window': false,
    'enable-sticky-header': true,
  };
}

export function conversionQueryOptionsUpdate(
  options: Partial<FunnelsQuery>,
  previousOptions: FunnelsQuery,
): Partial<FunnelsQuery> {
  let paramsToUpdate: Partial<FunnelsQuery> = options;
  if (options.exclusionSteps) {
    paramsToUpdate = {exclusionSteps: options.exclusionSteps};
  } else {
    if (
      [CountType.Unique, CountType.General].includes(previousOptions.countType) &&
      options.countType === CountType.Session
    ) {
      addToast({message: `Counting Sessions requires a conversion window of 1 session`});
    }

    if (!previousOptions.isAnyOrderFunnel() && options.funnelOrder === FunnelOrder.Any) {
      paramsToUpdate.events = previousOptions.getDefaultAnyOrderSteps();
    }
  }
  return paramsToUpdate;
}

export function queryBuilderUpdate(
  update: QueryBuilderUpdateDetail<QueryEventEntry>,
  funnelQuery: FunnelsQuery,
  isNewFunnel = false,
): {query: Partial<FunnelsQuery>; forceQuery: boolean; queryReason} {
  let entries = update.entries;
  const {dataGroupId, exclusionSteps, filters, requery, attributionSelection, groupBy, queryReason} = update;
  const changedBreakdowns = `groupBy` in update.delta;

  const isSessionStartAndEnd =
    entries.length === 2 && entries[0].event.value === `$session_start` && entries[1].event.value === `$session_end`;
  if (isSessionStartAndEnd && isNewFunnel) {
    addToast({message: `Counting Sessions requires a conversion window of 1 session`});
  }
  // If we select the last step and delete a step, reset the step selector
  const shouldResetEventIndex =
    !!funnelQuery.events && funnelQuery.events.length > entries.length && funnelQuery.eventIndex === entries.length - 1;

  let funnelAttributionSelection: FunnelAttribution = funnelQuery.funnelAttributionSelection;

  if (attributionSelection) {
    if (Number.isInteger(attributionSelection)) {
      funnelAttributionSelection = {action_mode: `step`, action_step: Number(attributionSelection)};
    } else {
      funnelAttributionSelection = {
        action_mode: attributionSelection as FunnelAttribution[`action_mode`],
      };
    }
  }

  // when dragging or duplicating events with a placeholder in the event list, we remove
  // the extra placeholder event to prevent invalid states
  if (entries.length >= 3) {
    entries = entries
      .filter((entry) => Boolean(entry.event.value) || Array.isArray(entry.event))
      .map((entry) => {
        // Event comparisons need special logic
        if (Array.isArray(entry.event)) {
          const filtered = entry.event.filter((nestedEntry) => Boolean(nestedEntry.event.value));
          return filtered.length === 1 ? filtered[0] : {...entry, event: filtered};
        } else {
          return entry;
        }
      });
  }

  return {
    query: {
      events: entries,
      exclusionSteps,
      filters: filters.map((filter) => new PropertyFilter(filter)),
      funnelAttributionSelection,
      groupBy,
      ...(changedBreakdowns ? {controlGroup: []} : {}),
      ...(shouldResetEventIndex ? {eventIndex: null} : {}),
      ...(isSessionStartAndEnd && isNewFunnel
        ? {countType: CountType.Session, conversionWindow: {value: 1, unit: ConversionWindowUnit.Session}}
        : {}),
      ...(`dataGroupId` in update ? {dataGroupId} : {}),
    },

    forceQuery: requery,
    queryReason,
  };
}

export function attributionItems({
  events,
  funnelAttributionSelection: currentAttribution,
}: FunnelsQuery): EventAttributionItem[] {
  const entries = events || [];
  const stepItems = entries.map((_, index) => ({
    label: `Step ${index + 1}`,
    value: index + 1,
    isSelected: false,
  }));
  const items = ATTRIBUTION_OPTIONS.concat(stepItems);

  let selectedIndex = items
    .map((item) => item.value, 10)
    .indexOf(
      currentAttribution?.action_mode === `step` ? currentAttribution?.action_step : currentAttribution?.action_mode,
    );
  if (selectedIndex < 0) {
    selectedIndex = 0;
  }
  return items.map((item, index) => ({
    ...item,
    value: item.value,
    index: String(index),
    isSelected: index === selectedIndex,
    tooltip: ATTRIBUTION_TYPE_TOOLTIPS[item.value],
  }));
}

export function getReportQueryContext(query: FunnelsQuery, originDashboard: DashboardOrigin): QueryContext {
  if (!query.hasChanges()) {
    return null;
  }

  const dateRange = query.dateRange.getAttributes();
  const dataGroupId = query.dataGroupId;

  const events: QueryEventEntry[] = [];
  query.events
    .filter((entry) => (entry.event as EventItem)?.value)
    .forEach(function (entry) {
      if (isGroupedEntry(entry)) {
        entry.event.forEach(function (ent) {
          events.push({
            type: ResourceType.Event,
            event: ent.event,
            filters: ent.filters,
            filtersOperator: ent.filtersOperator,
          });
        });
      } else {
        events.push({
          type: ResourceType.Event,
          event: entry.event,
          filters: entry.filters,
          filtersOperator: entry.filtersOperator,
        });
      }
    });

  const segments = query.groupBy;
  const comparedDateRange = query.comparedDateRange;
  const filters = query.filters;

  const conversionCriteria: ConversionCriteria = {
    conversionWindow: query.conversionWindow,
    countType: query.countType,
    exclusionSteps: query.exclusionSteps,
    holdingPropertyConstant: query.holdingPropertyConstant,
  };
  const extraParams = useSingleReport()
    ? {
        metricType: MetricType.Funnel,
        chartType: FunnelToCommonChartTypeMap[query.chartType],
      }
    : {};

  return {
    bookmarkType: BookmarkType.Funnels,
    dataGroupId,
    dateRange,
    querySamplingEnabled: query.querySampling,
    ...(events.length && {events}),
    ...(comparedDateRange && {comparedDateRange}),
    ...(originDashboard && {originDashboard}),
    ...(segments && {segments}),
    ...(filters && {filters}),
    ...{conversionCriteria},
    ...extraParams,
  };
}

export function getQueryFromQueryContext(queryContext: QueryContext): FunnelsQuery {
  const numSegmentColumns = queryContext.segments?.length || 1;
  const sorting = getDefaultFunnelStepsSortConfig(numSegmentColumns);

  let events = queryContext.events;
  if (queryContext.viewAsFunnelContext) {
    events = [];
    const selectedSteps = queryContext.viewAsFunnelContext.selectedSteps;
    let selectedStepIndex = 0;
    queryContext.viewAsFunnelContext.topPathsFlowSteps.forEach((event, idx) => {
      if (selectedSteps.has(idx)) {
        events.push(queryContext.events[selectedStepIndex]);
        selectedStepIndex++;
      } else {
        events.push({
          event: {
            id: null,
            label: event,
            value: event,
          },
          filters: [],
          filtersOperator: FiltersOperator.And,
          type: ResourceType.Event,
        });
      }
    });
    // Add selected steps that are not included in top paths
    while (selectedStepIndex < queryContext.events.length) {
      events.push(queryContext.events[selectedStepIndex]);
      selectedStepIndex += 1;
    }
  }

  return new FunnelsQuery({
    ...(useSingleReport() && queryContext.chartType && {chartType: CommonToFunnelChartTypeMap[queryContext.chartType]}),
    ...(queryContext.dataGroupId && {dataGroupId: queryContext.dataGroupId}),
    ...(queryContext.dateRange && {dateRange: queryContext.dateRange}),
    ...(queryContext.events && {events}),
    ...(queryContext.comparedDateRange && {comparedDateRange: queryContext.comparedDateRange}),
    ...(queryContext.originDashboard && {originDashboard: queryContext.originDashboard}),
    ...(queryContext.segments && {groupBy: queryContext.segments}),
    ...(queryContext.filters && {filters: queryContext.filters}),
    ...(queryContext.querySamplingEnabled && {querySampling: true}),
    ...queryContext.conversionCriteria,
    sorting,
  });
}
